import { useContext, useEffect, useState } from 'react';

import { ctxPopup } from '@/contexts/PopupContext';
import { DateRangeSelect as DateSelect } from '@/components/common/date/DateRangeSelect';
import { SingleTargetSelector } from '@/components/common/SingleTargetSelector';
import { TargetNameAndDescription, targetNameAndDescriptionStyle } from '@/components/common/TargetNameAndDescription';
import type { MinimalTarget } from '@/generated/model';

import type { RefuelsDateRange } from '../../types/RefuelsDateRange.types';
import type { RefuelsQueryResultProps } from '../../types/RefuelsQueryResultProps.types';
import { RefuelResultPanel } from '../refuel-result-panel/RefuelResultPanel';
import { RefuelsErrorState } from '../refuels-error-state/RefuelsErrorState';

import { TargetSelectorColumn, ResultColumn, DateSelectRow } from './RefuelsDesktopLayout.styles';

type RefuelsDesktopLayoutProps = RefuelsQueryResultProps & {
  hasBlockingError: boolean;
  selectedTarget: MinimalTarget | undefined;
  selectedDates: RefuelsDateRange | undefined;
  onTargetSelect: (target: MinimalTarget) => void;
  onDatesSelect: (from: Date, to: Date) => void;
  exportFileName: string;
};

export function RefuelsDesktopLayout({
  result,
  isFetching,
  onRetry,
  hasBlockingError,
  selectedTarget,
  selectedDates,
  onTargetSelect,
  onDatesSelect,
  exportFileName,
}: RefuelsDesktopLayoutProps) {
  const ctxPopups = useContext(ctxPopup);
  const [searchText, setSearchText] = useState<string>();

  const showLoader = ctxPopups?.showLoader;
  const hideLoader = ctxPopups?.hideLoader;

  useEffect(() => {
    if (!isFetching) {
      return;
    }

    showLoader?.();
    return () => hideLoader?.();
  }, [hideLoader, isFetching, showLoader]);

  function handleTargetSelect(target: MinimalTarget | undefined) {
    if (!target) {
      return;
    }

    onTargetSelect(target);
  }

  return (
    <>
      <TargetSelectorColumn>
        <SingleTargetSelector
          setSelectedTarget={handleTargetSelect}
          selectedTarget={selectedTarget}
          searchText={searchText}
          setSearchText={setSearchText}
        />
      </TargetSelectorColumn>
      {selectedTarget && (
        <ResultColumn data-qa-element-id="refuel_result_panel">
          <TargetNameAndDescription
            targetName={selectedTarget.name}
            targetDescription={selectedTarget.description}
            styleClassName={targetNameAndDescriptionStyle(undefined, undefined)}
          />
          <DateSelectRow>
            <DateSelect
              setSelectedDates={onDatesSelect}
              currentValue={selectedDates ?? { from: null, to: null }}
              onSameRangeSelect={onRetry}
            />
          </DateSelectRow>
          {hasBlockingError ? (
            <RefuelsErrorState isRetrying={isFetching} onRetry={onRetry} />
          ) : (
            <RefuelResultPanel
              // Remount on vehicle changes to reset RefuelDataGrid's local search text and grid state.
              key={selectedTarget.id}
              result={result}
              isFetching={isFetching}
              isListView={false}
              exportFileName={exportFileName}
            />
          )}
        </ResultColumn>
      )}
    </>
  );
}
